import {
  Badge,
  Card,
  HStack,
  Icon,
  IconButton,
  Skeleton,
  Stack,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useClipboard,
} from '@chakra-ui/react';
import {
  ChartPieIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  DocumentDuplicateIcon,
  FolderIcon,
  PencilIcon,
} from '@heroicons/react/outline';
import { CheckCircleIcon } from '@heroicons/react/solid';
import {
  ColumnFiltersState,
  SortingState,
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslations } from 'use-intl';

import { IdentityVerificationStatus, routes } from '@blockpulse3/data/shared';
import {
  GetAllCompaniesQuery,
  IdentityType,
  useGetAllCompaniesQuery,
} from '@blockpulse3/graphql/hooks';
import { formatDate, formatNumberCurrency } from '@blockpulse3/helpers';
import { IdentityAvatar, TableContainer, useSuccessToast } from '@blockpulse3/ui/commons';

import { Filter, fuzzyFilter } from '../TableUtils';

type Props = unknown;

/**
 * AdminCompaniesTable.
 * Display companies in a Table
 *
 * @returns {JSX.Element}
 */
export function AdminCompaniesTable(): JSX.Element {
  const i18nAdminTable = useTranslations('AdminList.Companies.AdminTable');
  const i18nBadge = useTranslations('AdminList.Companies.AdminTable.Badge');
  const navigate = useNavigate();
  const { data, loading } = useGetAllCompaniesQuery();

  const { onCopy, value, setValue, hasCopied } = useClipboard('');
  const successToast = useSuccessToast();

  useEffect(() => {
    if (!value) return;

    onCopy();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  useEffect(() => {
    if (!hasCopied) return;

    successToast({ title: `Id: ${value} copié` });
  }, [hasCopied, successToast, value]);

  const getColorBadge = useMemo(
    () =>
      (status: IdentityVerificationStatus): { color: string; label: string } => {
        switch (status) {
          case 'APPROVED':
            return { color: 'green', label: i18nBadge(status) };
          case 'PENDING':
          case 'PROCESSING':
            return { color: 'yellow', label: i18nBadge(status) };
          case 'REFUSED':
            return { color: 'red', label: i18nBadge(status) };
          default:
            return { color: 'gray', label: i18nBadge('NONE') };
        }
      },
    [i18nBadge],
  );

  const handleCopy = useCallback(
    (id: string): void => {
      setValue(id);
    },
    [setValue],
  );

  const handleRowEditClick = useCallback(
    (id: string): void => {
      navigate(routes.admin.company.settings.fmt(id));
    },
    [navigate],
  );

  const handleRowDocumentsClick = useCallback(
    (id: string): void => {
      navigate(routes.admin.company.documents.fmt(id));
    },
    [navigate],
  );

  const handleRowCaptableClick = useCallback(
    (id: string): void => {
      navigate(routes.admin.company.captable.fmt(id));
    },
    [navigate],
  );

  const [sorting, setSorting] = useState<SortingState>([{ id: 'identity', desc: false }]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  // const [globalFilter, setGlobalFilter] = useState('');

  const columnHelper = useMemo(
    () => createColumnHelper<GetAllCompaniesQuery['allCompanies'][0]>(),
    [],
  );
  const columns = useMemo(
    () => [
      columnHelper.accessor(
        (row) =>
          row.identity ? `${row.name} ${row.registrationNumber} ${row.identity.email}` : '',
        {
          id: 'identity',
          header: i18nAdminTable('Identity'),
          enableSorting: true,
          enableColumnFilter: true,
          cell: (info) => {
            const company = info.row.original;
            return (
              <Td>
                <HStack spacing="2.5">
                  <IdentityAvatar type={IdentityType.COMPANY} />
                  <Stack maxWidth="300px" spacing="1">
                    <HStack spacing="1">
                      <Text fontWeight="600">{company.name}</Text>
                      {company.identity?.isVerified && (
                        <Icon as={CheckCircleIcon} boxSize="15px" color="green.400" />
                      )}
                      <Text fontWeight="500">({company.registrationNumber})</Text>
                    </HStack>
                    <Text
                      color="gray.500"
                      fontWeight="400"
                      sx={{
                        textWrap: 'wrap',
                      }}
                    >
                      {company.identity?.email}
                    </Text>
                  </Stack>
                </HStack>
              </Td>
            );
          },
          filterFn: fuzzyFilter,
        },
      ),
      columnHelper.accessor('id', {
        header: 'id',
        enableSorting: false,
        enableColumnFilter: false,
        cell: (row) => (
          <Td>
            <HStack maxWidth="250px" spacing="1">
              <Text isTruncated color="gray.500" maxW="100px">
                {row.getValue()}
              </Text>
              <Icon
                as={DocumentDuplicateIcon}
                boxSize="20px"
                cursor="pointer"
                onClick={(): void => handleCopy(row.getValue())}
              />
            </HStack>
          </Td>
        ),
      }),
      columnHelper.accessor(() => 'actions', {
        header: 'Actions',
        enableSorting: false,
        enableColumnFilter: false,
        cell: (info) => {
          const data = info.row.original;
          return (
            <Td>
              <HStack spacing="1">
                <IconButton
                  aria-label="Éditer"
                  icon={<Icon as={PencilIcon} boxSize="18px" color="gray.700" />}
                  variant="secondary"
                  onClick={(): void => handleRowEditClick(data.id)}
                ></IconButton>
                <IconButton
                  aria-label="Documents"
                  icon={<Icon as={FolderIcon} boxSize="18px" color="gray.700" />}
                  isDisabled={!data.identity?.id}
                  variant="secondary"
                  onClick={(): void => handleRowDocumentsClick(data.id)}
                ></IconButton>
                <IconButton
                  aria-label="Actionnariat"
                  icon={<Icon as={ChartPieIcon} boxSize="18px" color="gray.700" />}
                  variant="secondary"
                  onClick={(): void => handleRowCaptableClick(data.id)}
                ></IconButton>
              </HStack>
            </Td>
          );
        },
      }),
      columnHelper.accessor((row) => row.kybVerificationStatus || '-', {
        id: 'kybVerificationStatus',
        header: i18nAdminTable('KYBStatus'),
        enableSorting: true,
        enableColumnFilter: false,
        cell: (row) => {
          const badge = getColorBadge(row.getValue() as IdentityVerificationStatus);
          return (
            <Td>
              <Badge colorScheme={badge.color}>{badge.label}</Badge>
            </Td>
          );
        },
      }),
      columnHelper.accessor('creationDate', {
        header: i18nAdminTable('CreationDate'),
        enableSorting: true,
        enableColumnFilter: false,
        cell: (row) => (
          <Td align="right">{row.getValue() ? formatDate(row.getValue(), 'DD/MM/YYYY') : '-'}</Td>
        ),
      }),
      columnHelper.accessor('shareCapital', {
        header: i18nAdminTable('ShareCapital'),
        enableSorting: true,
        enableColumnFilter: false,
        cell: (row) => {
          const shareCapital = row.getValue() || 0;
          return <Td isNumeric>{shareCapital ? formatNumberCurrency(shareCapital) : '-'}</Td>;
        },
      }),
      columnHelper.accessor('nominalValue', {
        header: i18nAdminTable('NominalValue'),
        enableSorting: true,
        enableColumnFilter: false,
        cell: (row) => {
          const nominalValue = row.getValue() || 0;
          return <Td isNumeric>{nominalValue ? formatNumberCurrency(nominalValue) : '-'}</Td>;
        },
      }),
      columnHelper.accessor((row) => row.capitalType || '-', {
        id: 'capitalType',
        header: i18nAdminTable('CapitalType'),
        enableSorting: true,
        enableColumnFilter: false,
        cell: (row) => <Td>{row.getValue()}</Td>,
      }),
      columnHelper.accessor((row) => row.type || '-', {
        id: 'type',
        header: i18nAdminTable('Type'),
        enableSorting: true,
        enableColumnFilter: false,
        cell: (row) => <Td>{row.getValue()}</Td>,
      }),
    ],
    [
      columnHelper,
      i18nAdminTable,
      getColorBadge,
      handleCopy,
      handleRowEditClick,
      handleRowDocumentsClick,
      handleRowCaptableClick,
    ],
  );

  const table = useReactTable({
    data: data?.allCompanies?.length ? data?.allCompanies : [],
    columns,
    filterFns: {
      fuzzy: fuzzyFilter,
    },
    state: {
      sorting,
      columnFilters,
      // globalFilter,
    },
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    // onGlobalFilterChange: setGlobalFilter,
    // globalFilterFn: fuzzyFilter,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
  });

  if (loading) {
    return (
      <Skeleton>
        <Card h="400px" />
      </Skeleton>
    );
  }

  return (
    <Stack spacing="4">
      {/* <DebouncedInput
        value={globalFilter ?? ''}
        onChange={(value) => setGlobalFilter(String(value))}
        placeholder="Search all columns..."
        maxW="sm"
        size="sm"
        rounded="md"
      /> */}
      <TableContainer maxH="none">
        <Table variant="striped">
          <Thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <Tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <Th key={header.id}>
                    <HStack justifyContent="space-between" spacing="2">
                      <HStack
                        cursor={header.column.getCanSort() ? 'pointer' : 'initial'}
                        spacing="0"
                        onClick={header.column.getToggleSortingHandler()}
                      >
                        <Text>
                          {header.isPlaceholder
                            ? null
                            : flexRender(header.column.columnDef.header, header.getContext())}
                        </Text>
                        {header.column.getIsSorted() && (
                          <Icon
                            boxSize="16px"
                            as={
                              header.column.getIsSorted() === 'asc'
                                ? ChevronUpIcon
                                : ChevronDownIcon
                            }
                          />
                        )}
                      </HStack>
                      {header.column.getCanFilter() && (
                        <Filter column={header.column} table={table} />
                      )}
                    </HStack>
                  </Th>
                ))}
              </Tr>
            ))}
          </Thead>
          <Tbody>
            {table.getRowModel().rows.map((row) => (
              <Tr key={row.id}>
                {row.getVisibleCells().map((cell) =>
                  flexRender(cell.column.columnDef.cell, {
                    ...cell.getContext(),
                    key: cell.id,
                  }),
                )}
              </Tr>
            ))}
          </Tbody>
        </Table>
      </TableContainer>
    </Stack>
  );
}

export type AdminCompaniesTableProps = Props;
